import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { 
  Row, 
  Col, 
  Typography, 
  Button, 
  Breadcrumb, 
  Skeleton, 
  Modal, 
  Form, 
  Input, 
  Upload, 
  message,
  Card,
  Space
} from 'antd';
import { UploadOutlined, EditOutlined } from "@ant-design/icons";
import ProductGallery from './Gallery';
import './SupplierProfile.scss';
import MarketPlaceService from '../../utils/api/marketplace';
import SupplierService from '../../utils/api/suppliers';
import LayoutPage from '../../layouts/Main';

const { Title, Text } = Typography;

export default function Supplier(props) {
  const user = JSON.parse(localStorage.getItem("user"));
  const { supplier_id } = useParams();
  const role = localStorage.getItem('role');
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingVendor, setLoadingVendor] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  const handleUpdateSupplier = async (values) => {
    try {
      let bannerImg = vendor.role_data?.banner_img;
      
      if (values.banner_img && values.banner_img.fileList.length > 0) {
        const file = values.banner_img.fileList[0];
        bannerImg = file.thumbUrl || URL.createObjectURL(file.originFileObj);
      }

      const updatedSupplier = {
        userId: supplier_id,
        banner_img: bannerImg,
        description: values.description,
        mision: values.mision,
        vision: values.vision,
      };

      await SupplierService.Update(updatedSupplier);
      
      setVendor(prev => ({
        ...prev,
        role_data: {
          ...prev.role_data,
          ...updatedSupplier
        }
      }));
      
      message.success("Proveedor actualizado con éxito");
      setEditModalVisible(false);
    } catch (error) {
      console.error("Error:", error);
      message.error("Error al actualizar el proveedor");
    }
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  const handleProductClick = (product) => {
    if (product === 'view-all') {
      navigate(`/marketplace/suppliers/${supplier_id}/all-products`);
    } else {
      navigate(`/marketplace/${product.category}/${product.sub_category}/${product.id}?name=${encodeURIComponent(product.name)}`);
    }
  };

  const loadVendorData = async () => {
    try {
      setLoadingVendor(true);
      const vendorResp = await MarketPlaceService.getVendorByUser(supplier_id);
      setVendor(vendorResp.data);
    } catch (error) {
      console.error("Error loading vendor:", error);
      message.error("Error al cargar los datos del proveedor");
    } finally {
      setLoadingVendor(false);
    }
  };

  const loadProductsData = async () => {
    try {
      setLoadingProducts(true);
      const productsResp = await MarketPlaceService.getProductsBySupplier(supplier_id);
      setProducts(productsResp.data?.products || []);
    } catch (error) {
      console.error("Error loading products:", error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadVendorData();
    loadProductsData();
  }, [supplier_id]);

  const openEditModal = () => {
    form.setFieldsValue({
      description: vendor?.role_data?.description || '',
      mision: vendor?.role_data?.mision || '',
      vision: vendor?.role_data?.vision || '',
    });
    setEditModalVisible(true);
  };

  if (loadingVendor) {
    return (
      <LayoutPage>
        <div className="supplier-profile">
          <div style={{ padding: '24px' }}>
            <Skeleton.Avatar active size={150} shape="circle" />
            <Skeleton active title={false} paragraph={{ rows: 4 }} />
          </div>
        </div>
      </LayoutPage>
    );
  }

  if (!vendor) {
    return (
      <LayoutPage>
        <div className="supplier-profile">
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Title level={3}>Proveedor no encontrado</Title>
            <Button type="primary" onClick={() => navigate('/marketplace')}>
              Volver al Marketplace
            </Button>
          </div>
        </div>
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <div className="supplier-profile">
        {/* Header con Banner y Logo */}
        <div className="supplier-header">
          <img
            src={vendor.role_data?.banner_img || "https://placehold.co/1200x300/1890ff/ffffff?text=Banner+del+Proveedor"}
            alt="Banner del proveedor"
            className="supplier-banner"
          />
          <img
            src={vendor.logo || "https://placehold.co/150/1890ff/ffffff?text=Logo"}
            alt="Logo del proveedor"
            className="supplier-logo"
          />
        </div>

        {/* Breadcrumb */}
        <div className="supplier-breadcrumb">
          <Breadcrumb>
            <Breadcrumb.Item 
              className="breadcrumb-item" 
              onClick={() => handleBreadcrumbClick('/marketplace')}
            >
              Inicio
            </Breadcrumb.Item>
            {role === '1' && (
              <Breadcrumb.Item 
                className="breadcrumb-item" 
                onClick={() => handleBreadcrumbClick('/marketplace/suppliers')}
              >
                Proveedores
              </Breadcrumb.Item>
            )}
            <Breadcrumb.Item>{vendor.bussiness}</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Información Principal */}
        <div className="supplier-info">
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={16}>
              <div className="supplier-main-info">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Title level={1} className="supplier-title">
                      {vendor.bussiness}
                    </Title>
                    {user?.id === supplier_id && (
                      <Button 
                        type="primary" 
                        icon={<EditOutlined />}
                        onClick={openEditModal}
                        className="edit-button"
                      >
                        Editar información
                      </Button>
                    )}
                  </div>
                  
                  <div className="supplier-description">
                    <Text strong>Descripción: </Text>
                    {vendor.role_data?.description || 'Este proveedor no ha agregado una descripción.'}
                  </div>

                  <Row gutter={[24, 24]} className="supplier-details">
                    <Col xs={24} sm={12}>
                      <div className="detail-section">
                        <div className="detail-label">Categoría principal</div>
                        <div className="detail-value">
                          {vendor.vendor?.category || 'No especificada'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="detail-section">
                        <div className="detail-label">Subcategorías</div>
                        <div className="detail-value">
                          {vendor.vendor?.sub_category || 'No especificadas'}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Space>
              </div>
            </Col>

            <Col xs={24} lg={8}>
              <Row gutter={[16, 16]} className="mission-vision-section">
                <Col xs={24}>
                  <Card className="mission-vision-card">
                    <Title level={4} className="card-title">Misión</Title>
                    <div className="card-content">
                      {vendor.role_data?.mision || 'El proveedor no ha definido su misión.'}
                    </div>
                  </Card>
                </Col>
                <Col xs={24}>
                  <Card className="mission-vision-card">
                    <Title level={4} className="card-title">Visión</Title>
                    <div className="card-content">
                      {vendor.role_data?.vision || 'El proveedor no ha definido su visión.'}
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        {/* Galería de Productos */}
        <div style={{ padding: '24px' }}>
          <ProductGallery 
            products={products} 
            handleClick={handleProductClick}
            loading={loadingProducts}
          />
        </div>
      </div>

      {/* Modal de Edición */}
      <Modal
        title="Editar Información del Proveedor"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
        className="supplier-edit-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateSupplier}
        >
          <Form.Item
            name="banner_img"
            label="Imagen de Banner"
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Subir Banner</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: 'Por favor ingresa una descripción' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Describe los servicios y especialidades de tu empresa..."
            />
          </Form.Item>

          <Form.Item
            name="mision"
            label="Misión"
            rules={[{ required: true, message: 'Por favor ingresa la misión' }]}
          >
            <Input.TextArea 
              rows={3} 
              placeholder="¿Cuál es el propósito de tu empresa?"
            />
          </Form.Item>

          <Form.Item
            name="vision"
            label="Visión"
            rules={[{ required: true, message: 'Por favor ingresa la visión' }]}
          >
            <Input.TextArea 
              rows={3} 
              placeholder="¿Hacia dónde quieres llevar tu empresa en el futuro?"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Actualizar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </LayoutPage>
  );
}